/**
 * F3: Forward-Adapter Angebot → GAEB-Anfrage (Großhandel/Subunternehmer).
 *
 * Baut aus den Angebots-Zeilen (QuoteItem-artig, F2-Struktur: gewerk = Titel,
 * untergewerkName = Untertitel) die GAEBEntry-Liste für den bestehenden
 * X83-Export-Writer (server/routes/gaeb-export.ts, /api/gaeb/export-partial):
 *   - title-Einträge: Ebene 1 (Gewerk) und Ebene 2 (Untertitel), parentId-Kette
 *   - item-Einträge: OHNE Preisfelder (ourPrice/originalPrice) — der Writer lässt
 *     mit omitPrices=true UP/IT weg (GAEB-Anfrage-Semantik: Preise trägt der Bieter ein)
 *
 * OZ-Quelle: computeQuoteNumbering (dieselbe Funktion wie der "Neu nummerieren"-Button)
 * — hat der Nutzer im Editor neu nummeriert, stimmen Editor- und Anfrage-OZ überein.
 * BEWUSSTE GRENZE: trägt das Angebot importierte Alt-OZ (numberingMode 'manual'),
 * bekommt die ANFRAGE frisch vergebene OZ (1 / 1.1 / 1.2.1) — die gespeicherten
 * Angebotsdaten bleiben unangetastet (reine Ausgabe, keine Mutation). Bei Teilmengen
 * entstehen bewusst Lücken, weil das vollständige Angebotsuniversum nummeriert wird.
 *
 * BEWUSSTE GRENZE (Alternativposition): DA83 bräuchte ALNGroupNo/ALNSerNo mit
 * Grundpositions-Verknüpfung, die unser flaches Flag nicht hat (TODO seit F1).
 * Alternativpositionen werden als normale Positionen mit sichtbarem Langtext-Zusatz
 * "[Alternativposition]" exportiert. Bedarfspositionen trägt der Writer maschinenlesbar
 * als <Provis> (X83).
 */

import { computeQuoteNumbering, getOzParentPrefix, getSubtitleName } from './quote-numbering';
import { isExcludedFromMainSum, toSafeNumber } from './quote-totals';
import {
  assertExportItemId,
  assertExportOptionalBoolean,
  parseExportNonNegativeNumber,
} from './quote-export-validation';

export interface GaebRequestSourceItem {
  id: number;
  /** Produktive LV-Strukturen können explizite Gliederungszeilen enthalten. */
  type?: string | null;
  /** Alternative Header-Kennzeichnung für Nicht-GAEB-Quellen. */
  isHeader?: boolean;
  gewerk?: string | null;
  untergewerkName?: string | null;
  name?: string | null;
  beschreibung?: string | null;
  einheit?: string | null;
  menge?: number | string | null;
  isBedarfsposition?: boolean;
  isAlternativposition?: boolean;
}

/** GAEBEntry-kompatible Minimalform, die der Export-Writer versteht. */
export interface GaebRequestEntry {
  type: 'title' | 'item';
  positionNumber: string;
  artikelId: string;
  parentId?: string;
  hierarchyLevel: number;
  name: string;
  shortText: string;
  beschreibung?: string;
  einheit?: string;
  menge?: number;
  isBedarfsposition?: boolean;
  // KEINE ourPrice/originalPrice-Felder: die Anfrage enthält keine Preise.
}

export interface GaebRequestBuildResult {
  entries: GaebRequestEntry[];
  stats: {
    titleCount: number;
    subtitleCount: number;
    itemCount: number;
    bedarfCount: number;
    alternativCount: number;
  };
}

export interface GaebRequestBuildOptions {
  /**
   * Vollständige Angebotsstruktur in Anzeige-Reihenfolge. `sourceItems` bleibt die
   * Exportauswahl und liefert deren Inhalte/Mengen; diese Zeilen liefern Struktur/OZ.
   */
  numberingRows?: ReadonlyArray<GaebRequestSourceItem>;
}

function assertValidUniqueIds(
  items: ReadonlyArray<GaebRequestSourceItem>,
  inputName: 'sourceItems' | 'numberingRows',
  options: { requirePositiveMenge?: boolean } = {},
): void {
  const seen = new Set<number>();
  for (let index = 0; index < items.length; index += 1) {
    const id = items[index]?.id;
    assertExportItemId(id, `${inputName}[${index}].id`);
    if (seen.has(id)) {
      throw new Error(`${inputName} enthält die doppelte ID ${id} an Index ${index}`);
    }
    seen.add(id);

    const item = items[index];
    assertExportOptionalBoolean(item?.isBedarfsposition, `${inputName}[${index}].isBedarfsposition`);
    assertExportOptionalBoolean(item?.isAlternativposition, `${inputName}[${index}].isAlternativposition`);
    // B-07: Nur exportierte, SUMMENWIRKSAME Leistungszeilen brauchen eine
    // Menge > 0. Drei bewusste Ausnahmen, alle getestet:
    //   1. Gliederungszeilen (Titel/Header) sind Struktur, keine Leistung — sie
    //      tragen legitim Menge 0 und werden ohnehin herausgefiltert.
    //   2. Bedarfs-/Alternativpositionen sind per Definition nicht
    //      summenwirksam (isExcludedFromMainSum). Menge 0 ist dort ein
    //      belegter Bestandszustand: server/routes/softec-export.ts behandelt
    //      "Bedarfsposition mit menge=0" ausdrücklich und exportiert sie mit 1.
    //      Sie hier abzulehnen würde Bestandsangebote unexportierbar machen.
    //   3. `numberingRows` liefert ausschliesslich Struktur und OZ. Eine
    //      Menge-0-Zeile, die gar nicht exportiert wird, darf eine gültige
    //      Auswahl nicht blockieren.
    const requirePositive = options.requirePositiveMenge === true
      && item !== undefined
      && !isGaebHeaderRow(item)
      // Nur die beiden Flags übergeben: `LineLike.menge` kennt kein null,
      // `isExcludedFromMainSum` liest die Menge ohnehin nicht.
      && !isExcludedFromMainSum({
        isBedarfsposition: item.isBedarfsposition,
        isAlternativposition: item.isAlternativposition,
      });
    parseExportNonNegativeNumber(item?.menge, `${inputName}[${index}].menge`, {
      optional: true,
      allowNumericString: true,
      requirePositive,
    });
  }
}

/**
 * Gliederungszeile statt Leistung. Exportiert, weil die UI vor dem Export
 * dieselbe Unterscheidung treffen muss, um eine wahrheitsgemässe Meldung zu
 * zeigen (B-07) — sonst zählt sie Titelzeilen als Positionen ohne Menge.
 */
export function isGaebHeaderRow(item: GaebRequestSourceItem): boolean {
  return item.type === 'title' || item.isHeader === true;
}

/**
 * Code-point-sichere Kürzung: substring/slice würden ein Surrogatpaar (z.B. Emoji) an
 * der Grenze zerschneiden → unpaired surrogate → der XML-Builder (allowSurrogateChars:
 * false) wirft "Invalid character" und der Export scheitert (Codex-P2). Array.from
 * iteriert nach Unicode-Code-Points und hält Paare zusammen.
 */
export function truncateCodePoints(value: string, max: number): string {
  if (max <= 0) return '';
  const cps = Array.from(value);
  return cps.length <= max ? value : cps.slice(0, max).join('');
}

// GAEB OutlineText: der Writer (convertEntryToGAEBItem) dokumentiert, dass überlanger
// Outline-Text ORCA AVA zum Absturz bringt → auf 150 Zeichen begrenzen. Gilt für BEIDE
// Quellen (Name UND Beschreibungs-Fallback), nicht nur den Fallback (Codex-P2).
const MAX_OUTLINE_TEXT = 150;
function buildShortText(item: GaebRequestSourceItem): string {
  const name = typeof item.name === 'string' ? item.name.trim() : '';
  if (name) return truncateCodePoints(name, MAX_OUTLINE_TEXT);
  const beschreibung = typeof item.beschreibung === 'string' ? item.beschreibung.trim() : '';
  if (beschreibung) return truncateCodePoints(beschreibung, MAX_OUTLINE_TEXT);
  return 'Position';
}

// DA83-QU ist tgNormalizedString4 (max. 4 Zeichen) — lange Editor-Einheiten wie
// "Pauschal" machen das XML schema-invalide (Codex-P2). Übliche GAEB-Kürzel mappen,
// Rest wird auf 4 Zeichen gekürzt (dokumentierter Fallback — nichts wird abgelehnt).
const GAEB_UNIT_MAP: Record<string, string> = {
  'pauschal': 'psch',
  'pausch.': 'psch',
  'stück': 'St',
  'stueck': 'St',
  'stunde': 'h',
  'stunden': 'h',
  'std': 'h',
  'std.': 'h',
  'meter': 'm',
  'quadratmeter': 'm2',
  'qm': 'm2',
  'm²': 'm2',
  'kubikmeter': 'm3',
  'm³': 'm3',
  'laufmeter': 'lfm',
  'lfdm': 'lfm',
  'kilogramm': 'kg',
  'tonne': 't',
  'tonnen': 't',
  'liter': 'l',
  'minute': 'min',
  'minuten': 'min',
};

export function normalizeGaebUnit(einheit: unknown): string {
  const raw = typeof einheit === 'string' ? einheit.trim() : '';
  if (!raw) return 'Stk';
  const mapped = GAEB_UNIT_MAP[raw.toLowerCase()];
  if (mapped) return mapped;
  // Fallback: auf 4 Code-Points kürzen (tgNormalizedString4, surrogate-sicher).
  return truncateCodePoints(raw, 4).trim() || 'Stk';
}

/** Sammel-Untertitel für direkte Positionen in GEMISCHTEN Gewerken (Codex-P1):
 * DA83-tgBoQBody ist xs:choice — ein BoQBody darf ENTWEDER Itemlist ODER BoQCtgy
 * enthalten, nie beides. Enthält ein Gewerk direkte UND untertitelte Positionen,
 * bekämen beide Zweige denselben BoQBody → Schema-Fehler. Lösung: die direkten
 * Positionen erhalten (nur in der Adapter-KOPIE) diesen Pseudo-Untertitel — das
 * Gewerk ist dann einheitlich gegliedert. GAEB-übliche Praxis ("Allgemein"-Titel). */
export const GAEB_PSEUDO_SUBTITLE = 'Allgemeine Positionen';

/**
 * Vereinheitlicht gemischte Gewerke VOR der Nummerierung (pure — arbeitet auf Kopien):
 * direkte Positionen eines Gewerks, das auch Untertitel hat, bekommen den
 * Pseudo-Untertitel. Läuft VOR computeQuoteNumbering → EINE Nummernquelle, die OZ
 * der Anfrage sind in sich konsistent (1.1.1 unter "1.1 Allgemeine Positionen").
 * BEWUSSTE FOLGE: im gemischten Fall weichen Anfrage-OZ von Editor-OZ ab (dort 1.1
 * zweiteilig) — Editor/PDF bleiben unberührt; die Anfrage ist ohnehin ein neues
 * Dokument (siehe OZ-Grenze im Dateikopf).
 */
function normalizeForGaebStructure(
  items: ReadonlyArray<GaebRequestSourceItem>,
): GaebRequestSourceItem[] {
  // Gewerke mit mindestens einem echten Untertitel (Metadatum-Regel via getSubtitleName).
  const gewerkeWithSubtitles = new Set<string>();
  const gewerkeWithDirect = new Set<string>();
  for (const item of items) {
    const key = typeof item.gewerk === 'string' ? item.gewerk.trim() : '';
    if (getSubtitleName(item)) {
      gewerkeWithSubtitles.add(key);
    } else {
      gewerkeWithDirect.add(key);
    }
  }

  return items.map((item) => {
    const key = typeof item.gewerk === 'string' ? item.gewerk.trim() : '';
    if (getSubtitleName(item) || !gewerkeWithSubtitles.has(key) || !gewerkeWithDirect.has(key)) {
      return item; // untertitelt ODER Gewerk ist nicht gemischt → unverändert
    }
    // Randfall: heißt das Gewerk selbst wie der Pseudo-Untertitel, würde die
    // Metadatum-Regel (untergewerkName===gewerk) ihn wieder als "kein Untertitel" werten.
    const pseudo = key === GAEB_PSEUDO_SUBTITLE ? 'Weitere Positionen' : GAEB_PSEUDO_SUBTITLE;
    return { ...item, untergewerkName: pseudo };
  });
}

/**
 * Baut die GAEB-Anfrage-Einträge. REINE Funktion: mutiert die Eingaben nicht
 * (Invariante: Export ändert NIE gespeicherte Angebotsdaten).
 */
export function buildGaebRequestEntries(
  sourceItems: ReadonlyArray<GaebRequestSourceItem>,
  gewerkOrder: ReadonlyArray<string> = [],
  options: GaebRequestBuildOptions = {},
): GaebRequestBuildResult {
  const effectiveNumberingRows = options.numberingRows ?? sourceItems;
  // IDs sind die fachliche Join-Invariante zwischen Auswahl und Vollstruktur. Ein
  // kollabierender Map-/Set-Key darf weder Werte überschreiben noch doppelte OZ bauen.
  assertValidUniqueIds(sourceItems, 'sourceItems', { requirePositiveMenge: true });
  assertValidUniqueIds(effectiveNumberingRows, 'numberingRows');

  // Explizite Titel-/Headerzeilen sind bereits Strukturrepräsentationen und niemals
  // nummerierbare Leistungen. Zentral filtern, damit auch Nicht-React-Aufrufer sicher
  // sind. Eine echte Auswahlzeile mit derselben ID wird anschließend wieder ergänzt.
  const selectedSourceItems = sourceItems.filter((item) => !isGaebHeaderRow(item));

  // Das vollständige Universum bestimmt Reihenfolge, Hierarchie und OZ. Eine gewählte
  // Zeile, die darin noch fehlt (z.B. kurzzeitig divergierende Client-Zustände), wird
  // defensiv genau einmal hinten ergänzt und dadurch niemals lautlos verworfen.
  const numberingUniverse = effectiveNumberingRows.filter((item) => !isGaebHeaderRow(item));
  const numberingIds = new Set(numberingUniverse.map((item) => item.id));
  for (const item of selectedSourceItems) {
    if (numberingIds.has(item.id)) continue;
    numberingUniverse.push(item);
    numberingIds.add(item.id);
  }

  // Codex-P1: gemischte Gewerke im VOLLSTÄNDIGEN Universum vereinheitlichen und erst
  // danach nummerieren. So bleibt die Pseudo-Untertitel-Struktur auch bei einem Subset
  // kanonisch und der Writer erzeugt nie Itemlist + BoQCtgy in demselben BoQBody.
  const normalizedUniverse = normalizeForGaebStructure(numberingUniverse);
  const numbering = computeQuoteNumbering(normalizedUniverse, gewerkOrder);
  const selectedById = new Map(selectedSourceItems.map((item) => [item.id, item] as const));
  const items = normalizedUniverse
    .filter((structureItem) => selectedById.has(structureItem.id))
    .map((structureItem) => ({
      ...selectedById.get(structureItem.id)!,
      // Strukturmerkmale stammen ausdrücklich aus dem Nummerierungsuniversum.
      gewerk: structureItem.gewerk,
      untergewerkName: structureItem.untergewerkName,
    }));

  // Nur Ahnen tatsächlich exportierter Positionen ausgeben; leere Kategorien sind
  // weder fachlich hilfreich noch für eine valide GAEB-Hierarchie erforderlich.
  const requiredAncestorOzs = new Set<string>();
  for (const item of items) {
    let parentOz = getOzParentPrefix(numbering.byId.get(item.id));
    while (parentOz) {
      requiredAncestorOzs.add(parentOz);
      parentOz = getOzParentPrefix(parentOz);
    }
  }

  const entries: GaebRequestEntry[] = [];

  // Ebene 1: Titel (Gewerke). categoryMap-Key im Writer = positionNumber.
  for (const title of numbering.titles) {
    if (!requiredAncestorOzs.has(title.oz)) continue;
    entries.push({
      type: 'title',
      positionNumber: title.oz,
      artikelId: `T-${title.oz}`,
      hierarchyLevel: 0,
      name: title.gewerk || 'Positionen',
      shortText: title.gewerk || 'Positionen',
    });
  }

  // Ebene 2: Untertitel (parentId = OZ des Eltern-Titels).
  for (const subtitle of numbering.subtitles) {
    if (!requiredAncestorOzs.has(subtitle.oz)) continue;
    entries.push({
      type: 'title',
      positionNumber: subtitle.oz,
      artikelId: `T-${subtitle.oz}`,
      parentId: getOzParentPrefix(subtitle.oz),
      hierarchyLevel: 1,
      name: subtitle.untergewerkName,
      shortText: subtitle.untergewerkName,
    });
  }

  // Positionen: parentId = Eltern-Präfix der OZ (Titel bei 1.1, Untertitel bei 1.2.1).
  let bedarfCount = 0;
  let alternativCount = 0;
  for (const item of items) {
    const oz = numbering.byId.get(item.id);
    if (!oz) continue; // Zeile ohne Gruppe (sollte nicht vorkommen — defensive)

    const isBedarf = Boolean(item.isBedarfsposition);
    const isAlternativ = Boolean(item.isAlternativposition);
    if (isBedarf) bedarfCount += 1;
    if (isAlternativ) alternativCount += 1;

    const beschreibungRaw = typeof item.beschreibung === 'string' ? item.beschreibung.trim() : '';
    // Alternativposition: sichtbarer Zusatz im Langtext (siehe Grenze im Dateikopf).
    const beschreibung = isAlternativ
      ? `${beschreibungRaw}${beschreibungRaw ? '\n' : ''}[Alternativposition]`
      : beschreibungRaw;

    entries.push({
      type: 'item',
      positionNumber: oz,
      artikelId: `P-${oz}`,
      parentId: getOzParentPrefix(oz),
      hierarchyLevel: oz.split('.').length - 1,
      name: buildShortText(item),
      shortText: buildShortText(item),
      beschreibung: beschreibung || buildShortText(item),
      einheit: normalizeGaebUnit(item.einheit),
      menge: Math.max(0, toSafeNumber(item.menge, 0)),
      isBedarfsposition: isBedarf || undefined,
    });
  }

  return {
    entries,
    stats: {
      titleCount: entries.filter((e) => e.type === 'title' && e.hierarchyLevel === 0).length,
      subtitleCount: entries.filter((e) => e.type === 'title' && e.hierarchyLevel === 1).length,
      itemCount: entries.filter((e) => e.type === 'item').length,
      bedarfCount,
      alternativCount,
    },
  };
}
